// `/admin/invites` (AUTH-06, croqui `screens/admin/convites.tsx`). Foco declarado: **a
// tabela de convites**. Quem convida é o admin; ninguém de fora se cadastra.
//
// Aqui ficam os dados e as escritas; a tela é `features/admin-invites/invites-screen.tsx`
// e o formulário é o drawer `NovoConvite` do croqui.
//
// As duas escritas passam por `ADMIN_STEP_UP_ROUTES`: o `403 mfa_required` abre o
// `StepUpDialog` dentro de `api/client.ts` e a chamada original é reexecutada sozinha;
// nada disso é reimplementado aqui (`spec/17` §4.3).

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import {
  type AdminInvite,
  adminInvitesKeys,
  adminInvitesListOptions,
  createInvite,
  revokeInvite,
} from '../api/queries/admin-invites.ts';
import { adminCountersQueryOptions } from '../api/queries/shell.ts';
import { ConfirmDialog } from '../components/app/confirm-dialog.tsx';
import { PageActions } from '../components/app/shell/page-slot.tsx';
import { Btn } from '../components/kit/btn.tsx';
import { InvitesScreen } from '../features/admin-invites/invites-screen.tsx';
import { NewInviteDrawer } from '../features/admin-invites/new-invite-drawer.tsx';
import {
  DEFAULT_EXPIRY_DAYS,
  inviteBodyOf,
  type NewInviteValues,
  TAB_IDS,
} from '../features/admin-invites/status.ts';

/** A aba é o `status` da URL; `all` é valor, não ausência (mesma regra de `/admin/withdrawals`). */
const searchSchema = z.object({
  status: z.enum(TAB_IDS).default('all').catch('all'),
});

function AdminInvites() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { status: tab } = Route.useSearch();
  const list = useInfiniteQuery(adminInvitesListOptions(tab === 'all' ? undefined : tab));
  // Mesma query da sidebar do admin (`['admin-counters']`): a aba "Pendentes" e o badge
  // do menu mostram o mesmo número, sem request a mais.
  const counters = useQuery(adminCountersQueryOptions());

  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [revoking, setRevoking] = useState<AdminInvite | undefined>(undefined);

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: adminInvitesKeys.all }),
      queryClient.invalidateQueries({ queryKey: adminCountersQueryOptions().queryKey }),
    ]);
  };

  /** Convidar e reenviar são a mesma chamada: a API revoga o ativo e gera outro token. */
  const invite = useMutation({
    mutationFn: async (values: NewInviteValues) => createInvite(inviteBodyOf(values)),
    onSuccess: async () => {
      setFormOpen(false);
      await refresh();
    },
  });

  const revoke = useMutation({
    mutationFn: async (row: AdminInvite) => {
      await revokeInvite(row.id);
    },
    onSuccess: async () => {
      setRevoking(undefined);
      await refresh();
    },
  });

  const openForm = (): void => {
    invite.reset();
    setFormOpen(true);
  };

  const rows = list.data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <>
      <PageActions>
        <Btn kind="primary" icon="lock" onClick={openForm}>
          Convidar seller
        </Btn>
      </PageActions>

      <InvitesScreen
        rows={rows}
        tab={tab}
        onTab={(next) => {
          void navigate({ to: '/admin/invites', search: { status: next } });
        }}
        search={search}
        onSearch={setSearch}
        {...(counters.data === undefined ? {} : { pendingCount: counters.data.invites_pending })}
        isLoading={list.isPending}
        // Erro só quando não há lista: com o polling de 30 s, um flake no refetch de
        // fundo trocaria a lista carregada por uma tela de falha.
        error={list.data === undefined ? list.error : undefined}
        onRetry={() => {
          void list.refetch();
        }}
        hasMore={list.hasNextPage}
        isLoadingMore={list.isFetchingNextPage}
        onLoadMore={() => {
          void list.fetchNextPage();
        }}
        onInvite={openForm}
        onResend={(row) => {
          invite.mutate({
            email: row.email,
            store_name: row.store_name ?? '',
            expires_in_days: DEFAULT_EXPIRY_DAYS,
          });
        }}
        onRevoke={(row) => {
          revoke.reset();
          setRevoking(row);
        }}
        busy={invite.isPending || revoke.isPending}
      />

      <NewInviteDrawer
        open={formOpen}
        isPending={invite.isPending}
        error={invite.error}
        onOpenChange={setFormOpen}
        onSubmit={(values) => {
          invite.mutate(values);
        }}
      />

      <ConfirmDialog
        open={revoking !== undefined}
        title="Revogar convite"
        description={`O link de ${revoking?.email ?? ''} para de funcionar na hora. Você pode convidar de novo depois.`}
        confirmLabel="Revogar"
        destructive
        isPending={revoke.isPending}
        error={revoke.error}
        onConfirm={() => {
          if (revoking !== undefined) {
            revoke.mutate(revoking);
          }
        }}
        onOpenChange={(open) => {
          if (!open) {
            setRevoking(undefined);
          }
        }}
      />
    </>
  );
}

export const Route = createFileRoute('/admin/invites')({
  component: AdminInvites,
  validateSearch: searchSchema,
});
